/**
 * Browser-specific Image Utilities
 * Uses DOM APIs (canvas, ImageData, etc.)
 */

import { FacebetterError } from '../core/errors.js';

/**
 * Converts various image sources to ImageData (browser version)
 * @param {ImageData|HTMLImageElement|HTMLCanvasElement|HTMLVideoElement|Uint8ClampedArray} source - Image source
 * @param {number} width - Image width (required if source is Uint8ClampedArray)
 * @param {number} height - Image height (required if source is Uint8ClampedArray)
 * @returns {ImageData} ImageData object
 */
export function toImageData(source, width, height) {
  if (source instanceof ImageData) {
    return source;
  }

  if (source instanceof HTMLCanvasElement || source instanceof HTMLVideoElement) {
    const canvas = source instanceof HTMLCanvasElement ? source : document.createElement('canvas');
    if (source instanceof HTMLVideoElement) {
      canvas.width = source.videoWidth;
      canvas.height = source.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(source, 0, 0);
    }
    const ctx = canvas.getContext('2d');
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }

  if (source instanceof HTMLImageElement) {
    const canvas = document.createElement('canvas');
    canvas.width = source.naturalWidth || source.width;
    canvas.height = source.naturalHeight || source.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(source, 0, 0);
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }

  if (source instanceof Uint8ClampedArray) {
    if (width === undefined || height === undefined) {
      throw new FacebetterError('Width and height are required when source is Uint8ClampedArray');
    }
    return new ImageData(source, width, height);
  }

  throw new FacebetterError('Unsupported image source type');
}

/**
 * Gets User-Agent string for web platform
 * @returns {string} User-Agent string
 */
export function getUserAgentString() {
  return 'FB/1.0 (web; wasm32)';
}

/**
 * Generates HMAC-SHA256 signature (browser version)
 * @param {string} key - HMAC key (app_key)
 * @param {string} data - Data to sign (timestamp string)
 * @returns {Promise<string>} Promise that resolves with hex-encoded signature
 */
export async function generateHMACSignature(key, data) {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(data);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);

  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(signature));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Calls online_auth API to verify app_key (browser version)
 * @param {string} appId - Application ID
 * @param {string} appKey - Application key
 * @returns {Promise<Object>} Promise that resolves with response data
 */
export async function verifyAppKeyOnline(appId, appKey) {
  try {
    const timestamp = Math.floor(Date.now() / 1000);
    const timestampStr = timestamp.toString();
    const hmacSignature = await generateHMACSignature(appKey, timestampStr);

    const requestBody = {
      p_app_id: appId,
      p_hmac_signature: hmacSignature,
      p_timestamp: timestamp,
      p_user_agent: getUserAgentString()
    };

    const response = await fetch('https://facebetter.pixpark.net/rest/v1/rpc/online_auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': getUserAgentString()
      },
      body: JSON.stringify(requestBody)
    });

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('Online auth request failed:', error);
    return null;
  }
}

